import React from "react";

/**
 * Wrap any component tree with this to stop an uncaught render error from
 * blanking the entire page. Instead of the whole app disappearing, only this
 * boundary's children are replaced with a visible error panel — and critically,
 * that panel shows the actual error message and component stack, so we can see
 * exactly what's breaking without needing to open DevTools.
 *
 * Usage:
 *   import ErrorBoundary from "./ErrorBoundary";
 *   <ErrorBoundary label="Reports">
 *     <Reports />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    console.error("ErrorBoundary caught:", error, info);

    // Auto-recover from stale PWA caches or missing webpack chunks after new deployments
    const errMsg = String(error?.message || error || "");
    if (
      errMsg.includes("Loading chunk") ||
      errMsg.includes("ChunkLoadError") ||
      errMsg.includes("Unexpected token '<'") ||
      errMsg.includes("Failed to fetch dynamically imported module")
    ) {
      const lastReload = sessionStorage.getItem("dt_chunk_err_reload");
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem("dt_chunk_err_reload", String(now));
        if ("caches" in window) {
          caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n)))).finally(() => {
            window.location.reload();
          });
        } else {
          window.location.reload();
        }
      }
    }
  }

  render() {
    if (this.state.error) {
      const isChunkError = Boolean(
        String(this.state.error?.message || this.state.error || "").match(
          /Loading chunk|ChunkLoadError|Unexpected token '<'|Failed to fetch dynamically imported module/i
        )
      );

      if (isChunkError) {
        return (
          <div style={{ padding: 24, margin: "20px auto", maxWidth: 600, border: "1px solid #E2E8F0", borderRadius: 16, background: "#FFFFFF", boxShadow: "0 10px 30px rgba(0,0,0,0.06)", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <h4 style={{ color: "#181059", fontWeight: 800, marginBottom: 8 }}>
              Application Update Available
            </h4>
            <p style={{ color: "#64748B", fontSize: 14, marginBottom: 16 }}>
              A newer version of the application is available. Click below to refresh your session.
            </p>
            <button
              className="dt-btn dt-btn--primary px-4 py-2"
              onClick={() => {
                if ("caches" in window) {
                  caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n)))).finally(() => {
                    window.location.reload();
                  });
                } else {
                  window.location.reload();
                }
              }}
            >
              Refresh Application Now
            </button>
          </div>
        );
      }

      return (
        <div style={{ padding: 24, margin: "20px auto", maxWidth: 650, border: "1px solid #FED7AA", borderRadius: 16, background: "#FFF7ED", boxShadow: "0 4px 20px rgba(0,0,0,0.05)", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
          <h4 style={{ color: "#9A3412", fontWeight: 700, marginBottom: 8 }}>
            Unable to display this view
          </h4>
          <p style={{ color: "#7C2D12", fontSize: 13, marginBottom: 16 }}>
            {this.state.error?.message || "An unexpected error occurred while loading this section."}
          </p>
          <div className="d-flex justify-content-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm px-3"
              onClick={() => {
                this.setState({ error: null, info: null });
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  window.location.href = `${process.env.PUBLIC_URL}/dashboard`;
                }
              }}
            >
              ← Go Back
            </button>
            <button
              className="btn btn-warning btn-sm px-3 fw-bold"
              onClick={() => this.setState({ error: null, info: null })}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;